'use client';

import type { MonolithPayInValidationResponse, PaymentMethodDataEncCardForWorldpay } from '@payments/aurum-contracts';
import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { Button, FormField } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { createDebug } from '@shared/debug';
import { type BlockedBinRange, useBlockedBins, useSeonSession } from '@shared/hooks';
import { Spinner } from '@shared/icons';
import { NumberFormats as nf, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { DEPOSIT_PAYMENT_METHODS } from '../../../../../PaymentMethodSelector/PaymentMethods';
import { useMultistepForm } from '../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { TextInput } from '../../../../../form-controls/TextInput/TextInput';
import type { PaymentInstrumentsResponse } from '../../../../../my-account/Wallet/types';
import { useGlobalSheet } from '../../../../context/GlobalSheetContext';
import { type CardDetailsStepProps, type DepositTransactionPayload, type FormData, CardType } from '../../types';
import { CARD_SCHEME_PROFILES, CardScheme, caretPositionForDigit, getCardSchemeForNumber, groupDigits } from './cardSchemes';
import { useSeonCardEncryption } from './hooks/useSeonCardEncryption';
import { type RawCardData, useWorldpayCard } from './hooks/useWorldpayCard';

/** Builds a unique idempotency token for a deposit attempt. Kept at module scope so the time-based
 * (impure) value is generated at call time, never during render. */
function createIdempotencyToken(playerId?: string): string {
  return `${playerId}-${Date.now()}`;
}

/** Detects Visa/Mastercard by BIN for tracking purposes. */
function getCardType(cardNumber: string): CardType | null {
  if (!cardNumber) return null;

  const VISA_FIRST_NUMBER = '4';
  const MASTERCARD_FIRST_BLOCK_START = 2221;
  const MASTERCARD_FIRST_BLOCK_END = 2720;
  const MASTERCARD_SECOND_BLOCK_START = 5100;
  const MASTERCARD_SECOND_BLOCK_END = 5599;

  const firstNumber = cardNumber[0];
  if (firstNumber === VISA_FIRST_NUMBER) return CardType.VISA;
  if (cardNumber.length < 4) return null;
  const firstFourNumbers = parseInt(cardNumber.substring(0, 4));
  const isWithinMastercardFirstBlock = firstFourNumbers >= MASTERCARD_FIRST_BLOCK_START && firstFourNumbers <= MASTERCARD_FIRST_BLOCK_END;
  const isWithinMastercardSecondBlock = firstFourNumbers >= MASTERCARD_SECOND_BLOCK_START && firstFourNumbers <= MASTERCARD_SECOND_BLOCK_END;
  if (isWithinMastercardFirstBlock || isWithinMastercardSecondBlock) return CardType.MASTERCARD;
  return null;
}

function CardDetailsStep({
  onRegisterBackHandler,
  onError,
  onTransactionStart,
  onRegisterCleanupHandler,
  namespace,
  depositFlowInstance,
  setTrackingDetails,
  bundleCode,
  ...props
}: CardDetailsStepProps) {
  const debug = createDebug({ isEnabled: false });
  const t = useTranslations(namespace);
  const { goToPreviousStep, setIsLoading, isLoading } = useMultistepForm();
  const {
    watch,
    setValue,
    register,
    formState: { errors },
    trigger,
    setError,
    clearErrors,
  } = useFormContext<FormData>();
  const { encryptSeonCardData } = useSeonCardEncryption();
  const { getSeonSessionId } = useSeonSession();
  const { hashString, encrypt, getSessionId, getBrowserData, cleanup } = useWorldpayCard({
    onError: () => {
      setIsLoading(false);
      onError(t('aurumErrors.unknown'), 'PROCESSING_DEPOSIT');
    },
  });
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const { updateDepositSheetTitle } = useGlobalSheet();
  const formatter = useCurrencyFormatter();
  const { createDepositTransactionMutation, compoundId } = depositFlowInstance;
  const { acceptsAmexCards } = getSiteConfig().PAYMENTS_FLAGS;

  const amount = watch('amountStep.amount');
  const cardNumber = watch('methodStep.cardNumber');
  const expiryDate = watch('methodStep.expiryDate');
  const cvv = watch('methodStep.cvv');
  const cardholderName = watch('methodStep.cardholderName');

  const cardScheme = getCardSchemeForNumber(cardNumber || '', acceptsAmexCards);
  const cardProfile = CARD_SCHEME_PROFILES[cardScheme];
  const trackedCardType = cardScheme === CardScheme.AMEX ? CardType.AMEX : getCardType(cardNumber);

  // Optional cardholder-name hint — shown only in markets whose locale defines the message.
  const cardholderNameHint = t.has('card_details.cardholder_name.help') ? t('card_details.cardholder_name.help') : undefined;

  useEffect(() => {
    if (compoundId) {
      setTrackingDetails({
        transactionId: compoundId,
        method: 'worldpaycard',
        amount,
        provider: 'worldpaycard',
        offer: bundleCode || undefined,
      });
    }
  }, [compoundId, amount, bundleCode, setTrackingDetails]);

  useEffect(() => {
    updateDepositSheetTitle(t('card_details.title'));
  }, [updateDepositSheetTitle, t]);

  const { blockedBinsList, isLoading: isBlockedBinsLoading, error: blockedBinsError } = useBlockedBins();

  // Check for deposit validation API errors - this is checked when submitting from card details step
  const { error: paymentsLimitsError } = useApiQuery<MonolithPayInValidationResponse>(API_ROUTES.PAYMENTS.DEPOSIT_LIMITS, {
    credentials: true,
  });

  // Check for payment instruments API errors
  const { error: paymentInstrumentsError } = useApiQuery<PaymentInstrumentsResponse>(API_ROUTES.MY_ACCOUNT.PAYMENT_INSTRUMENTS, {
    credentials: true,
    queryOptions: {
      retry: false,
    },
  });

  // Log blocked bins loading state for debugging
  useEffect(() => {
    if (blockedBinsError) {
      debug.forceError('Failed to load blocked bins data:', blockedBinsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedBinsError]);

  const isCardNumberBlocked = useCallback((cardNumber: string, blockedBins: BlockedBinRange[]): boolean => {
    if (!cardNumber || cardNumber.length < 4) return false;

    return blockedBins.some(range => {
      // Determine the number of digits to check based on the range
      const firstDigits = range.first.toString().length;
      const lastDigits = range.last.toString().length;
      const digitsToCheck = Math.max(firstDigits, lastDigits);

      // Ensure we don't check more digits than available in the card number
      if (cardNumber.length < digitsToCheck) return false;

      // Extract the appropriate number of digits from the card number
      const cardBin = parseInt(cardNumber.substring(0, digitsToCheck), 10);

      return cardBin >= range.first && cardBin <= range.last;
    });
  }, []);

  // Validate card number against blocked bins
  const validateCardAgainstBlockedBins = useCallback(
    (cardNumber: string) => {
      if (!cardNumber || cardNumber.length < 4) return true;

      // Remove spaces from card number
      const cleanCardNumber = cardNumber.replace(/\s/g, '');

      // Check against all blocked bin ranges
      for (const blockedBin of blockedBinsList) {
        if (blockedBin.ranges && isCardNumberBlocked(cleanCardNumber, blockedBin.ranges)) {
          return false;
        }
      }

      return true;
    },
    [blockedBinsList, isCardNumberBlocked]
  );

  useEffect(() => {
    const backHandler = () => {
      // Clean up any active Worldpay operations before going back
      cleanup();
      // Allow going back to previous step
      goToPreviousStep();
      return true;
    };

    onRegisterBackHandler(backHandler);
  }, [onRegisterBackHandler, goToPreviousStep, cleanup]);

  // Register cleanup handler for retry functionality
  useEffect(() => {
    onRegisterCleanupHandler(cleanup);
  }, [onRegisterCleanupHandler, cleanup]);

  const validateAgainstPaymentMethodLimits = (): { isValid: boolean; errorMessage?: string } => {
    if (!amount) {
      return { isValid: true };
    }

    const cardPaymentMethod = DEPOSIT_PAYMENT_METHODS.CARD;
    const amountInPence = Math.round(Number(amount) * 100);
    const { minAllowedAmount, maxAllowedAmount } = cardPaymentMethod;

    if (minAllowedAmount !== undefined && amountInPence < minAllowedAmount) {
      const formattedMinAmount = formatter(minAllowedAmount).format(nf.WITH_DECIMALS).get();
      return {
        isValid: false,
        errorMessage: t('amount.min_amount_error', { amount: formattedMinAmount }),
      };
    }

    if (maxAllowedAmount !== undefined && amountInPence > maxAllowedAmount) {
      const formattedMaxAmount = formatter(maxAllowedAmount).format(nf.WITH_DECIMALS).get();
      return {
        isValid: false,
        errorMessage: t('amount.max_amount_error', { amount: formattedMaxAmount }),
      };
    }

    return { isValid: true };
  };

  const handleSetCardDetails = async () => {
    const isValid = await trigger('methodStep');
    if (!isValid) return;

    // Handle deposit validation API error
    if (paymentsLimitsError) {
      setIsLoading(false);
      onError(t('aurumErrors.unknown'), 'PROCESSING_DEPOSIT');
      return;
    }

    // Handle payment instruments API error
    if (paymentInstrumentsError) {
      setIsLoading(false);
      onError(t('aurumErrors.unknown'), 'PROCESSING_DEPOSIT');
      return;
    }

    // Handle blocked bins API error
    if (blockedBinsError) {
      setIsLoading(false);
      onError(t('aurumErrors.unknown'), 'PROCESSING_DEPOSIT');
      return;
    }

    const paymentMethodValidation = validateAgainstPaymentMethodLimits();
    if (!paymentMethodValidation.isValid) {
      setError('methodStep.cardNumber', {
        type: 'manual',
        message: paymentMethodValidation.errorMessage || '',
      });
      return;
    }

    clearErrors('methodStep.cardNumber');
    const isCardValid = validateCardAgainstBlockedBins(cardNumber);

    if (!isCardValid) {
      setError('methodStep.cardNumber', {
        type: 'manual',
        message: t('card_details.card_number.blocked_bin_error'),
      });
      return;
    }

    // Open new tab synchronously before any async operations to avoid popup blockers
    depositFlowInstance.openNewTab();

    setIsLoading(true);
    onTransactionStart();
    setValue('methodStep.cardNumber', cardNumber);
    setValue('methodStep.expiryDate', expiryDate);
    setValue('methodStep.cvv', cvv);
    setValue('methodStep.cardholderName', cardholderName);

    const rawCardData: RawCardData = {
      cardNumber: cardNumber.replace(/ /g, ''),
      expiryMonth: expiryDate.split('/')[0],
      expiryYear: '20' + expiryDate.split('/')[1],
      cvv,
      cardholderName,
    };

    const idempotencyToken = createIdempotencyToken(playerId);

    let payload: DepositTransactionPayload;
    try {
      const encryptedData = await encrypt(rawCardData);
      if (!encryptedData) {
        depositFlowInstance.closeNewTab();
        cleanup();
        setIsLoading(false);
        onError(t('aurumErrors.encryptionFailed'), 'PROCESSING_DEPOSIT');
        return;
      }

      const [encCardData, sessionId, browser] = await Promise.all([encryptSeonCardData(rawCardData), getSessionId(rawCardData, idempotencyToken), getBrowserData()]);
      const seonSessionId = await getSeonSessionId();
      const paymentMethodData: PaymentMethodDataEncCardForWorldpay = {
        ...encryptedData,
        encCardData,
        hash: hashString(cardNumber),
        sessionId,
        browser,
      };

      payload = {
        idempotencyToken,
        amount: Math.round(Number(amount) * 100),
        paymentMethod: 'worldpaycard',
        paymentMethodData: btoa(JSON.stringify(paymentMethodData)),
        acceptedTermsAndConditions: true,
        ...(seonSessionId && { seonSessionId }),
      };
    } catch (error) {
      debug.error('Error creating deposit transaction:', error);
      depositFlowInstance.closeNewTab();
      cleanup();
      setIsLoading(false);
      onError(t('aurumErrors.unknown'), 'PROCESSING_DEPOSIT');
      return;
    }

    // Set tracking details before mutation to ensure they're available even on immediate failure
    setTrackingDetails({
      method: 'Debit Card',
      provider: trackedCardType || 'unknown',
      amount,
      offer: bundleCode || undefined,
    });

    createDepositTransactionMutation.mutate(payload);
  };

  return (
    <div className="flex flex-col gap-6" data-testid="card-details-step" {...props}>
      <FormField
        name={'methodStep.cardNumber'}
        render={({ field }) => (
          <TextInput
            id="methodStep.cardNumber"
            label={t('card_details.card_number.label')}
            maxLength={19}
            placeholder={groupDigits('0'.repeat(cardProfile.cardDigits), cardProfile.groups)}
            type="text"
            inputMode="numeric"
            autoComplete="cc-number"
            disabled={isLoading}
            data-testid="card-number-input"
            {...register('methodStep.cardNumber', {
              onBlur: () => {
                trigger('methodStep.cardNumber');
                // Reformat the card number using the active scheme's digit grouping.
                const digitsOnly = field.value?.replace(/ /g, '') || '';
                const formattedCardNumber = groupDigits(digitsOnly, cardProfile.groups);
                field.onChange(formattedCardNumber);
              },
            })}
            onChange={e => {
              const input = e.target;
              const value = input.value;
              // Track the caret by how many digits precede it, so re-grouping doesn't move it to the end.
              const selectionStart = input.selectionStart ?? value.length;
              const digitsBeforeCaret = value.slice(0, selectionStart).replace(/\D/g, '').length;

              // Remove any non-digit characters and cap to the scheme's card length.
              const digitsOnly = value.replace(/\D/g, '').slice(0, cardProfile.cardDigits);

              // Format using the active scheme's digit grouping as the user types.
              const formattedValue = groupDigits(digitsOnly, cardProfile.groups);

              field.onChange(formattedValue);
              trigger('methodStep.cardNumber');

              // Restore the caret to the same digit boundary after React updates the input value.
              requestAnimationFrame(() => {
                if (document.activeElement !== input) return;
                const caret = caretPositionForDigit(formattedValue, digitsBeforeCaret);
                input.setSelectionRange(caret, caret);
              });
            }}
            value={field.value}
            helpMessage={errors?.methodStep?.cardNumber?.message}
            helpMessageType={errors?.methodStep?.cardNumber ? 'error' : undefined}
            inputTrackingProps={{
              'data-track-name': 'Card Number',
              'data-track-privacy': 'Mask',
            }}
            helperTrackingProps={{
              'data-track-helper': 'Card Number',
            }}
          />
        )}
      />

      <div className="grid grid-cols-3 gap-2">
        <FormField
          name={'methodStep.expiryDate'}
          render={({ field }) => (
            <TextInput
              id="methodStep.expiryDate"
              className="col-span-2"
              label={t('card_details.expiry_date.label')}
              maxLength={5}
              placeholder="MM/YY"
              type="text"
              autoComplete="cc-exp"
              inputMode="numeric"
              disabled={isLoading}
              data-testid="expiry-date-input"
              {...register('methodStep.expiryDate', {
                onBlur: () => {
                  const value = field.value || '';
                  const digitsOnly = value.replace(/\D/g, '');

                  // If month is greater than 12, correct it to 12
                  if (digitsOnly.length >= 2) {
                    const month = parseInt(digitsOnly.slice(0, 2), 10);
                    if (month > 12) {
                      const year = digitsOnly.slice(2, 4);
                      field.onChange('12' + (year ? '/' + year : ''));
                    }
                  }

                  trigger('methodStep.expiryDate');
                },
              })}
              onChange={e => {
                const value = e.target.value;
                // Remove any non-digit characters
                const digitsOnly = value.replace(/\D/g, '');

                // Prevent months greater than 12
                let monthDigits = digitsOnly.slice(0, 2);
                if (monthDigits.length === 2) {
                  const month = parseInt(monthDigits, 10);
                  if (month > 12) {
                    monthDigits = '12';
                  }
                }

                // Format with "/" after 2 digits
                let formattedValue = monthDigits;
                if (monthDigits.length >= 2) {
                  const yearDigits = digitsOnly.slice(2, 4);
                  formattedValue = monthDigits + '/' + yearDigits;
                }

                field.onChange(formattedValue);
              }}
              onKeyDown={e => {
                // Handle backspace when cursor is after "/"
                if (e.key === 'Backspace' && field.value && field.value.length > 0) {
                  const cursorPosition = e.currentTarget.selectionStart || 0;
                  const value = field.value;
                  const inputElement = e.currentTarget;

                  // If cursor is right after "/" (position 3), delete the "/" and the previous digit
                  if (cursorPosition === 3 && value[2] === '/') {
                    e.preventDefault();
                    const newValue = value.slice(0, 1) + value.slice(3);
                    field.onChange(newValue);
                    // Set cursor position after the first digit
                    setTimeout(() => {
                      inputElement.setSelectionRange(1, 1);
                    }, 0);
                  }
                }
              }}
              value={field.value}
              helpMessage={errors?.methodStep?.expiryDate?.message}
              helpMessageType={errors?.methodStep?.expiryDate ? 'error' : undefined}
              inputTrackingProps={{
                'data-track-name': 'Expiry Date',
                'data-track-privacy': 'Mask',
              }}
              helperTrackingProps={{
                'data-track-helper': 'Expiry Date',
              }}
            />
          )}
        />

        <FormField
          name={'methodStep.cvv'}
          render={({ field }) => (
            <TextInput
              id="methodStep.cvv"
              className="col-span-1"
              label={t('card_details.cvv.label')}
              maxLength={cardProfile.cvvDigits}
              placeholder={'0'.repeat(cardProfile.cvvDigits)}
              type="text"
              autoComplete="cc-csc"
              inputMode="numeric"
              disabled={isLoading}
              data-testid="cvv-input"
              {...register('methodStep.cvv', {
                onBlur: () => trigger('methodStep.cvv'),
              })}
              onChange={e => {
                const value = e.target.value;
                // Only accept digits
                const digitsOnly = value.replace(/\D/g, '');
                field.onChange(digitsOnly);
              }}
              value={field.value}
              helpMessage={errors?.methodStep?.cvv?.message}
              helpMessageType={errors?.methodStep?.cvv ? 'error' : undefined}
              inputTrackingProps={{
                'data-track-name': 'Security Code',
                'data-track-privacy': 'Mask',
              }}
              helperTrackingProps={{
                'data-track-helper': 'Security Code',
              }}
            />
          )}
        />
      </div>

      <FormField
        name={'methodStep.cardholderName'}
        render={({ field }) => (
          <TextInput
            id="methodStep.cardholderName"
            label={t('card_details.cardholder_name.label')}
            placeholder={t('card_details.cardholder_name.placeholder')}
            type="text"
            autoComplete="cc-name"
            disabled={isLoading}
            data-testid="cardholder-name-input"
            {...register('methodStep.cardholderName', {
              onBlur: () => trigger('methodStep.cardholderName'),
            })}
            onChange={e => {
              const value = e.target.value;
              // Remove digits from input
              const withoutDigits = value.replace(/\d/g, '');
              field.onChange(withoutDigits);
            }}
            value={field.value}
            helpMessage={errors?.methodStep?.cardholderName?.message ?? cardholderNameHint}
            helpMessageType={errors?.methodStep?.cardholderName ? 'error' : undefined}
            inputTrackingProps={{
              'data-track-name': 'Full Name',
              'data-track-privacy': 'Mask',
            }}
            helperTrackingProps={{
              'data-track-helper': 'Full Name',
            }}
          />
        )}
      />

      <div className="flex flex-col gap-4 mt-auto md:mt-0" data-track-container="Actions">
        {/* Apply button */}
        <Button
          type="button"
          variant="primary"
          onClick={handleSetCardDetails}
          disabled={isLoading || isBlockedBinsLoading}
          className="w-full h-10"
          data-testid="apply-button"
          data-track-name="Deposit"
          data-track-method="Debit Card"
          data-track-count="1"
          data-track-type="FTD"
          data-track-amount={amount}
          data-track-provider={trackedCardType}
          {...(bundleCode && { 'data-track-offer': bundleCode })}
        >
          {isLoading ? <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} /> : t('card_details.deposit_button', { amount })}
        </Button>
      </div>
    </div>
  );
}

export { CardDetailsStep };
